<template>
  <v-layout wrap>
    <v-flex xs12 xl12 pa-2>
      <v-card>
        <Subheader title="Populate RISC grades">
          <!-- <v-btn color="add" @click="addValue()">
            <v-icon class="mr-1">add</v-icon>
            {{ $t('Button.Add') }}
          </v-btn> -->
        </Subheader>
        <v-card-text>
          <v-alert color="info" icon="info" :value="true" outline>
            Populate the RISC grades and output configurations for the tenant. 
            <v-btn color="add" @click="populateValues()">
            <v-icon class="mr-1">add</v-icon>
            Populate
          </v-btn>
          </v-alert>
        </v-card-text>
      </v-card>
    </v-flex>
    <v-flex xs12 xl6 pa-2>
      <v-card pb-2>
        <Subheader title="Qualifier Values" v-model="searchValue" search="Qualifier Values">
          <v-btn color="add" @click="addValue()">
            <v-icon class="mr-1">add</v-icon>
            {{ $t('Button.Add') }}
          </v-btn>
        </Subheader>
        <v-card-text>
          <v-alert color="info" icon="info" :value="true" outline>
            Record continuous database data qualifier values that should be displayed. Any data qualifier values not included in this list will not be shown to the client. Once a continuous database data qualifier is recorded in this list, you can map the value to an output value in the <code>RISC Grade Output Configuration</code> table.
          </v-alert>
          <v-data-table :headers="valueHeaders" :items="ContinuousDataQualifiers" :search="searchValue" hide-actions>
            <template slot="headerCell" slot-scope="props">
              <v-tooltip bottom v-if="props.header.showtooltip">
                <template v-slot:activator="{ on }">
                  <span v-on="on" v-html="props.header.text"></span>
                </template>
                <v-card-text>{{$t('ContinuousDataQualifiersManage.menuTextYes')}}</v-card-text>
                <v-card-text>{{$t('ContinuousDataQualifiersManage.menuTextNo')}}</v-card-text>
              </v-tooltip>
              <span v-else>{{ props.header.text }}</span>
            </template>
            <template slot="items" slot-scope="props">
              <td class="text-xs-left">{{ props.item.QualifierValue }}</td>
              <td class="text-xs-left">
                <span v-if="props.item.ShouldFilter">
                  <v-chip disabled outline small color="error">filtered out</v-chip>
                </span>
                <span v-else>
                  <v-chip v-if="!haveConfiguration(props.item)" disabled outline small color="warning">output configuration needed</v-chip>
                  <v-chip v-else disabled outline small color="info">-</v-chip>
                </span>
              </td>
              <td class="text-xs-right">{{ props.item.Level }}</td>
              <td class="text-xs-right">
                <v-flex>
                  <EditButton small @click="editValue(props.item)">
                    {{ $t('Button.Edit') }}
                  </EditButton>
                  <DeleteButton small @click="deleteValue(props.item)">
                    {{ $t('Button.Delete') }}
                  </DeleteButton>
                </v-flex>
              </td>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-flex>
    <br />
    <v-flex xs12 xl6 pa-2>
      <v-card>
        <Subheader title="RISC Grade Output Configuration" v-model="searchOutput" search="RISC Grade Output Configuration">
          <v-btn color="add" @click="addOutput()">
            <v-icon class="mr-1">add</v-icon>
            {{ $t('Button.Add') }}
          </v-btn>
        </Subheader>
        <v-card-text>
          <v-alert color="info" icon="info" :value="true" outline>
            Designate client-facing data qualifier output values. Each output value can be mapped to multiple continuous database data qualifier value. Once mapped, the continuous database qualifier value will be translated to the code and description specified in this table in output reports and R client.
          </v-alert>
          <v-data-table :headers="outputHeaders" :items="ContinuousDataQualifierConfigurations" :search="searchOutput" hide-actions>
            <template slot="items" slot-scope="props">
              <td class="text-xs-left">{{ props.item.Code }}</td>
              <td class="text-xs-left">{{ props.item.Description }}</td>              
              <td class="text-xs-left" v-html="getValueString(props.item.ContinuousDataQualifiers).toString()"></td>
              <td class="text-xs-left">{{ props.item.Ordinal }}</td>
              <td class="text-xs-right">
                <v-flex>
                  <EditButton small @click="editOutput(props.item)">
                    {{ $t('Button.Edit') }}
                  </EditButton>
                  <DeleteButton small @click="deleteOutput(props.item)">
                    {{ $t('Button.Delete') }}
                  </DeleteButton>
                </v-flex>
              </td>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-flex>

    <app-modal v-model="addValueModal" max-width="800" persistent>
      <template v-slot:title>
        Add Qualifier Value
      </template>
      <template v-slot:text>
        <v-form v-model="validValue" ref="formValue" lazy-validation>
          <v-text-field label="Qualifier Value" v-model="addQualifierValue.QualifierValue"></v-text-field>
          Should Filter out:
          <v-menu offset-y open-on-hover :open-delay="400">
            <template v-slot:activator="{ on }">
              <v-icon small v-on="on" class="clickable ml-2" color="primary">info_outline</v-icon>
            </template>
            <v-card>
              <v-card-text>{{$t('ContinuousDataQualifiersManage.menuTextYes')}}</v-card-text>
              <v-card-text>{{$t('ContinuousDataQualifiersManage.menuTextNo')}}</v-card-text>
            </v-card>
          </v-menu>
          <v-switch v-model="addQualifierValue.ShouldFilter" hide-details :label="`${getFilterString(addQualifierValue.ShouldFilter).toString()}`"></v-switch>
          <v-text-field label="Level" v-model="addQualifierValue.Level" :rules="qualifierValueRule"></v-text-field>
        </v-form>
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn color="success" @click="submitAddValue()">
          <v-icon>done</v-icon>{{ $t('Button.Save') }}
        </v-btn>
        <v-btn color="secondary" @click="addValueModal = false" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </app-modal>

    <app-modal v-model="addOutputModal" max-width="800" persistent>
      <template v-slot:title>
        Add RISC Grade Output Configuration
      </template>
      <template v-slot:text>
        <v-form v-model="validOutput" ref="formOutput" lazy-validation>
          <v-text-field label="Code" v-model="addQualifierOutput.Code" :rules="nullRule"></v-text-field>
          <v-text-field label="Description" v-model="addQualifierOutput.Description" :rules="nullRule"></v-text-field>
          <v-text-field label="Ordinal" v-model="addQualifierOutput.Ordinal" :rules="nullRule"></v-text-field>
          <v-select label="Value" v-model="addQualifierOutput.Values" :items="allValueItem" multiple :rules="nullRuleSelect"></v-select>
        </v-form>
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn color="success" @click="submitAddOutput()">
          <v-icon>done</v-icon>{{ $t('Button.Save') }}
        </v-btn>
        <v-btn color="secondary" @click="addOutputModal = false" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </app-modal>

    <app-modal v-model="editValueModal" max-width="800" persistent>
      <template v-slot:title>
        Edit Qualifier Value
      </template>
      <template v-slot:text>
        <v-text-field label="Qualifier Value" v-model="editQualifierValue.QualifierValue"></v-text-field>
        Should Filter out:
        <v-menu offset-y open-on-hover :open-delay="400">
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on" class="clickable ml-2" color="primary">info_outline</v-icon>
          </template>
          <v-card>
            <v-card-text>{{$t('ContinuousDataQualifiersManage.menuTextYes')}}</v-card-text>
            <v-card-text>{{$t('ContinuousDataQualifiersManage.menuTextNo')}}</v-card-text>
          </v-card>
        </v-menu>
        <v-switch v-model="editQualifierValue.ShouldFilter" hide-details :label="`${getFilterString(editQualifierValue.ShouldFilter).toString()}`"></v-switch>
        <v-text-field label="Level" v-model="editQualifierValue.Level" :rules="qualifierValueRule"></v-text-field>
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn color="success" @click="submitEditValue()">
          <v-icon>done</v-icon>{{ $t('Button.Save') }}
        </v-btn>
        <v-btn color="secondary" @click="editValueModal = false" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </app-modal>

    <app-modal v-model="editOutputModal" max-width="800" persistent>
      <template v-slot:title>
        Edit RISC Grade Output Configuration
      </template>
      <template v-slot:text>
        <v-text-field label="Code" v-model="editQualifierOutput.Code" :rules="nullRule"></v-text-field>
        <v-text-field label="Description" v-model="editQualifierOutput.Description" :rules="nullRule"></v-text-field>
        <v-text-field label="Ordinal" v-model="editQualifierOutput.Ordinal" :rules="nullRule"></v-text-field>
        <v-select label="Value" v-model="editQualifierOutput.Values" :items="editOutputValueItem" multiple></v-select>
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn color="success" @click="submitEditOutput()">
          <v-icon>done</v-icon>{{ $t('Button.Save') }}
        </v-btn>
        <v-btn color="secondary" @click="editOutputModal = false" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </app-modal>

    <app-modal v-model="deleteValueModal" max-width="800" persistent>
      <template v-slot:title>
        Delete Qualifier Value
      </template>
      <template v-slot:text>
        Are you sure you want to delete qualifier value {{deleteValueName}}? If removed, data will not be recoverable.
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn dark color="delete" @click="submitDeleteValue()">
          <v-icon>delete_forever</v-icon>{{ $t('Button.Delete') }}
        </v-btn>
        <v-btn color="secondary" @click="deleteValueModal = false" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </app-modal>

    <app-modal v-model="deleteOutputModal" max-width="800" persistent>
      <template v-slot:title>
        Delete RISC Grade Output Configuration
      </template>
      <template v-slot:text>
        Are you sure you want to delete RISC grade output configuration {{deleteOutputName}}? If removed, data will not be recoverable.
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn dark color="delete" @click="submitDeleteOutput()">
          <v-icon>delete_forever</v-icon>{{ $t('Button.Delete') }}
        </v-btn>
        <v-btn color="secondary" @click="deleteOutputModal = false" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </app-modal>

    <app-confirmation-modal v-model="warningModel" max-width="500" persistent>
      <template v-slot:title>
        {{ errorTitle }}
      </template>
      <template v-slot:text>
        {{ errorMessage }}
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn color="secondary" @click="warningModel = false" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </app-confirmation-modal>

    <v-card color="grey lighten-4">
      <v-snackbar style="text-align:center" :color="snackColor" :timeout="null" top v-model="snackbar">
        {{ snackMessage }}
        <v-btn dark flat @click.native="snackbar = false">{{ $t('Button.Close') }}</v-btn>
      </v-snackbar>
    </v-card>
  </v-layout>
</template>

<script>
  import Subheader from '@/components/generic/Subheader'
  import AppModal from '@/components/generic/AppModal'
  import AppConfirmationModal from '@/components/generic/AppConfirmationModal'

  import {
    createNamespacedHelpers,
  } from 'vuex'
  const qualifier = createNamespacedHelpers('qualifiers')
  const authenticate = createNamespacedHelpers('authenticate')

  export default {
    components: {
      Subheader,
      'app-modal': AppModal,
      'app-confirmation-modal': AppConfirmationModal,
    },
    props: {
      tenantName: {
        type: String,
        default: '',
      },
      tenantId: {
        type: Number,
        default: null,
      },
    },
    data() {
      return {
        validValue: true,
        validOutput: true,
        valueHeaders: [{
          text: 'Qualifier Value',
          value: 'QualifierValue',
          align: 'left',
        },
        {
          text: 'Should Filter out <i class="v-icon clickable ml-1 material-icons theme--light primary--text" style="display:contents">info_outline</i>',
          value: 'ShouldFilter',
          align: 'left',
          showtooltip: true,
        },
        {
          text: 'Level',
          value: 'Level',
          align: 'right',
        },
        {
          text: 'Actions',
          value: null,
          align: 'right',
        },
        ],
        outputHeaders: [{
          text: 'Code',
          value: 'Code',
          align: 'left',
        },
        {
          text: 'Description',
          value: 'Description',
          align: 'left',
        },
        {
          text: 'Qualifiers Values',
          value: 'ContinuousDataQualifiers',
          align: 'left',
        },
        {
          text: 'Ordinal',
          value: 'Ordinal',
          align: 'left',
        },
        {
          text: 'Actions',
          value: null,
          align: 'right',
        },
        ],
        qualifierValueRule: [
          (v) => {
            if (v) {
              return /^\d+$/.test(v) || 'Qualifier Value must be numerical'
            } else
              return 'Is required, cannot be null'
          },
        ],
        nullRule: [
          (v) => !!v || 'Is required, cannot be null',
        ],
        nullRuleSelect: [
          (v) => !!v.length || 'Is required, cannot be null',
        ],
        menu: false,
        warningModel: false,
        errorTitle: '',
        errorMessage: '',
        snackbar: false,
        snackColor: '',
        snackMessage: '',
        searchValue: '',
        searchOutput: '',
        addValueModal: false,
        addOutputModal: false,
        editValueModal: false,
        editOutputModal: false,
        deleteValueModal: false,
        deleteOutputModal: false,
        addQualifierValue: {
          QualifierValue: null,
          ShouldFilter: false,
          Level: null,
        },
        editQualifierValue: {
          Id: null,
          QualifierValue: null,
          ShouldFilter: false,
          Level: null,
          QualifierConfigurationId: null,
        },
        deleteQualifierValueId: null,
        addQualifierOutput: {
          Code: '',
          Description: '',
          Ordinal: null,
          Values: [],
        },
        editQualifierOutput: {
          Id: null,
          Code: '',
          Description: '',
          Ordinal: null,
          Values: [],
        },
        deleteQualifierOutputId: null,
        editValueCheckList: [],
        editOutputCheckList: [],
        editOutputValueItem: [],
        deleteValueName: '',
        deleteOutputName: '',
      }
    },
    mounted() {
      if (!this.qualifierData || !this.qualifierData.length) {
        this.getQualifiersDataAndConfiguration(this.tenantId ? this.tenantId : this.currentUserGroup.id)
      }
    },
    computed: {
      ...qualifier.mapGetters(['ContinuousDataQualifiers', 'ContinuousDataQualifierConfigurations', 'allValueItem']),
      ...authenticate.mapGetters(['currentUserGroup']),
      titleName() {
        return `${this.tenantName ? this.tenantName : this.currentUserGroup.name} Continuous Data Qualifiers Management`
      },
    },
    methods: {
      ...qualifier.mapActions(['getQualifiersDataAndConfiguration', 'addQualifierData', 'updateQualifierData', 'deleteQualifierData', 'addQualifierConfiguration', 'updateQualifierConfiguration', 'deleteQualifierConfiguration', 'populateDefaultQualifers']),
      getFilterString(filter) {
        return filter ? 'Yes' : 'No'
      },
      getValueString(values) {
        let arr = []
        values.forEach(e => {
          arr.push(e.QualifierValue)
        })
        return arr
      },
      addValue() {
        this.$refs.formValue.resetValidation()
        this.addValueModal = true
        this.addQualifierValue = {
          QualifierValue: null,
          Level: null,
          ShouldFilter: false,
        }
      },
      addOutput() {
        this.$refs.formOutput.resetValidation()
        this.addOutputModal = true
        this.addGradeOutput = {
          Code: '',
          Description: '',
          Ordinal: null,
          Values: [],
        }
      },
      editValue(data) {
        this.$refs.formValue.resetValidation()
        let targetData = this.ContinuousDataQualifiers.find(x => x.Id == data.Id)
        if (!targetData.QualifierConfigurationId) {
          this.editValueModal = true
          this.editValueCheckList = JSON.parse(JSON.stringify(this.ContinuousDataQualifiers))
          this.editValueCheckList.splice(this.editValueCheckList.findIndex(x => x.QualifierValue == data.QualifierValue), 1)
          this.editQualifierValue = {
            Id: data.Id,
            QualifierValue: data.QualifierValue,
            ShouldFilter: data.ShouldFilter,
            Level: data.Level,
            QualifierConfigurationId: data.QualifierConfigurationId,
          }
        } else {
          this.warningModel = true
          this.errorTitle = this.$t('ContinuousDataQualifiersManage.unableUpdate')
          this.errorMessage = this.$t('ContinuousDataQualifiersManage.unableUpdateMsg')
        }
      },
      editOutput(data) {
        this.$refs.formOutput.resetValidation()
        let Values = []
        data.ContinuousDataQualifiers.forEach(x => Values.push(x.QualifierValue))
        this.editOutputModal = true
        this.editQualifierOutput.Id = data.Id
        this.editQualifierOutput.Code = data.Code
        this.editQualifierOutput.Description = data.Description
        this.editQualifierOutput.Ordinal = data.Ordinal
        this.editQualifierOutput.Values = Values
        this.editOutputCheckList = JSON.parse(JSON.stringify(this.ContinuousDataQualifierConfigurations))
        this.editOutputCheckList.splice(this.editOutputCheckList.findIndex(x => x.Code == data.Code), 1)
        this.editOutputValueItem = JSON.parse(JSON.stringify(this.allValueItem))
        Values.forEach(x => this.editOutputValueItem.push(x))
      },
      haveConfiguration(data) {
        let targetData = this.ContinuousDataQualifiers.find(x => x.Id == data.Id)
        if (!targetData.QualifierConfigurationId)
          return false
        else
          return true
      },
      deleteValue(data) {
        this.deleteValueName = ''
        let targetData = this.ContinuousDataQualifiers.find(x => x.Id == data.Id)
        if (!targetData.QualifierConfigurationId) {
          this.deleteValueModal = true
          this.deleteQualifierValueId = data.Id
          this.deleteValueName = data.QualifierValue
        } else {
          this.warningModel = true
          this.errorTitle = this.$t('ContinuousDataQualifiersManage.unableDelete')
          this.errorMessage = this.$t('ContinuousDataQualifiersManage.unableDeleteMsg')
        }
      },
      deleteOutput(data) {
        this.deleteOutputName = ''
        this.deleteOutputModal = true
        this.deleteQualifierOutputId = data.Id
        this.deleteOutputName = data.Code
      },
      submitAddValue() {
        if (this.addQualifierValue.QualifierValue || this.addQualifierValue.Level) {
          let repeat = this.ContinuousDataQualifiers.findIndex(e => e.QualifierValue == this.addQualifierValue.QualifierValue)
          if (repeat == -1) {
            let options = {
              data: {
                Id: null,
                QualifierValue: this.addQualifierValue.QualifierValue,
                ShouldFilter: this.addQualifierValue.ShouldFilter,
                Level: this.addQualifierValue.Level,
                TenantId: this.tenantId ? this.tenantId : this.currentUserGroup.id,
                QualifierConfigurationId: null,
              },
              successCb: () => {
                this.snackbar = true
                this.snackColor = 'success'
                this.snackMessage = this.$t('ContinuousDataQualifiersManage.saveSuccessMsg')
                this.addValueModal = false
                this.addQualifierValue = {
                  QualifierValue: null,
                  ShouldFilter: false,
                  Level: null,
                }
              },
              failedCb: (error) => {
                this.warningModel = true
                this.errorTitle = this.$t('ContinuousDataQualifiersManage.errorMessage')
                this.errorMessage = error
              },
            }
            this.addQualifierData(options)
          } else {
            this.snackbar = true
            this.snackColor = 'error'
            this.snackMessage = this.$t('ContinuousDataQualifiersManage.valueExists')
          }
        } else {
          this.snackbar = true
          this.snackColor = 'error'
          this.snackMessage = this.$t('ContinuousDataQualifiersManage.couldNotBeNull')
        }
      },
      submitAddOutput() {
        if (this.addQualifierOutput.Code && this.addQualifierOutput.Description && this.addQualifierOutput.Ordinal && this.addQualifierOutput.Values && this.addQualifierOutput.Values.length) {
          let repeat = this.ContinuousDataQualifierConfigurations.findIndex(e => e.Code == this.addQualifierOutput.Code)
          if (repeat == -1) {
            let qualifierList = []
            this.addQualifierOutput.Values.forEach(x => {
              qualifierList.push(this.ContinuousDataQualifiers.find(y => y.QualifierValue == x))
            })
            let options = {
              data: {
                Id: null,
                Code: this.addQualifierOutput.Code,
                Description: this.addQualifierOutput.Description,
                Ordinal: this.addQualifierOutput.Ordinal,
                TenantId: this.tenantId ? this.tenantId : this.currentUserGroup.id,
                ContinuousDataQualifiers: qualifierList,
              },
              successCb: () => {
                this.snackbar = true
                this.snackColor = 'success'
                this.snackMessage = this.$t('ContinuousDataQualifiersManage.saveSuccessMsg')
                this.addOutputModal = false
                this.addQualifierOutput = {
                  Code: '',
                  Description: '',
                  Ordinal: null,
                  Values: [],
                }
              },
              failedCb: (error) => {
                this.warningModel = true
                this.errorTitle = this.$t('ContinuousDataQualifiersManage.errorMessage')
                this.errorMessage = error
              },
            }
            this.addQualifierConfiguration(options)
          } else {
            this.snackbar = true
            this.snackColor = 'error'
            this.snackMessage = this.$t('ContinuousDataQualifiersManage.codeExists')
          }
        } else {
          this.snackbar = true
          this.snackColor = 'error'
          this.snackMessage = this.$t('ContinuousDataQualifiersManage.couldNotBeNull')
        }
      },
      submitEditValue() {
        if (this.editQualifierValue.QualifierValue) {
          if (this.editQualifierValue.QualifierConfigurationId && this.editQualifierValue.ShouldFilter) {
            this.warningModel = true
            this.errorTitle = this.$t('ContinuousDataQualifiersManage.errorMessage')
            this.errorMessage = this.$t('ContinuousDataQualifiersManage.shouldFilterOutCouldNotBeYes')
          } else {
            let repeat = this.editValueCheckList.findIndex(e => e.QualifierValue == this.editQualifierValue.QualifierValue)
            if (repeat == -1) {
              let options = {
                data: {
                  Id: this.editQualifierValue.Id,
                  QualifierValue: this.editQualifierValue.QualifierValue,
                  ShouldFilter: this.editQualifierValue.ShouldFilter,
                  TenantId: this.tenantId ? this.tenantId : this.currentUserGroup.id,
                  Level: this.editQualifierValue.Level,
                  QualifierConfigurationId: this.editQualifierValue.QualifierConfigurationId,
                },
                successCb: () => {
                  this.snackbar = true
                  this.snackColor = 'success'
                  this.snackMessage = this.$t('ContinuousDataQualifiersManage.saveSuccessMsg')
                  this.editValueModal = false
                  this.editQualifierValue = {
                    Id: null,
                    QualifierValue: null,
                    ShouldFilter: false,
                    Level: null,
                    QualifierConfigurationId: null,
                  }
                },
                failedCb: (error) => {
                  this.warningModel = true
                  this.errorTitle = this.$t('ContinuousDataQualifiersManage.errorMessage')
                  this.errorMessage = error
                },
              }
              this.updateQualifierData(options)
            } else {
              this.snackbar = true
              this.snackColor = 'error'
              this.snackMessage = this.$t('ContinuousDataQualifiersManage.valueExists')
            }
          }

        } else {
          this.snackbar = true
          this.snackColor = 'error'
          this.snackMessage = this.$t('ContinuousDataQualifiersManage.couldNotBeNull')
        }
      },
      submitEditOutput() {
        if (this.editQualifierOutput.Code && this.editQualifierOutput.Description) {
          let repeat = this.editOutputCheckList.findIndex(e => e.Code == this.editQualifierOutput.Code)
          if (repeat == -1) {
            let qualifierList = []
            this.editQualifierOutput.Values.forEach(x => {
              qualifierList.push(this.ContinuousDataQualifiers.find(y => y.QualifierValue == x))
            })
            let options = {
              data: {
                Id: this.editQualifierOutput.Id,
                Code: this.editQualifierOutput.Code,
                Description: this.editQualifierOutput.Description,
                Ordinal: this.editQualifierOutput.Ordinal,
                TenantId: this.tenantId ? this.tenantId : this.currentUserGroup.id,
                ContinuousDataQualifiers: qualifierList,
              },
              successCb: () => {
                this.snackbar = true
                this.snackColor = 'success'
                this.snackMessage = this.$t('ContinuousDataQualifiersManage.saveSuccessMsg')
                this.editOutputModal = false
                this.editQualifierOutput = {
                  Id: null,
                  Code: '',
                  Description: '',
                  Ordinal: null,
                  Values: [],
                }
              },
              failedCb: (error) => {
                this.warningModel = true
                this.errorTitle = this.$t('ContinuousDataQualifiersManage.errorMessage')
                this.errorMessage = error
              },
            }
            this.updateQualifierConfiguration(options)
          } else {
            this.snackbar = true
            this.snackColor = 'error'
            this.snackMessage = this.$t('ContinuousDataQualifiersManage.codeExists')
          }
        } else {
          this.snackbar = true
          this.snackColor = 'error'
          this.snackMessage = this.$t('ContinuousDataQualifiersManage.couldNotBeNull')
        }
      },
      submitDeleteValue() {
        let options = {
          data: this.deleteQualifierValueId,
          successCb: () => {
            this.snackbar = true
            this.snackColor = 'success'
            this.snackMessage = this.$t('ContinuousDataQualifiersManage.deleteSuccessMsg')
            this.deleteValueModal = false
            this.deleteGradeValueId = null
          },
          failedCb: (error) => {
            this.warningModel = true
            this.errorTitle = this.$t('ContinuousDataQualifiersManage.errorMessage')
            this.errorMessage = error
          },
        }
        this.deleteQualifierData(options)
      },
      submitDeleteOutput() {
        let options = {
          data: this.deleteQualifierOutputId,
          successCb: () => {
            this.snackbar = true
            this.snackColor = 'success'
            this.snackMessage = this.$t('ContinuousDataQualifiersManage.deleteSuccessMsg')
            this.deleteOutputModal = false
            this.deleteQualifierOutputId = null
          },
          failedCb: (error) => {
            this.warningModel = true
            this.errorTitle = this.$t('ContinuousDataQualifiersManage.errorMessage')
            this.errorMessage = error
          },
        }
        this.deleteQualifierConfiguration(options)
      },
      populateValues() {
        let options = {
          data: this.tenantId ? this.tenantId : this.currentUserGroup.id,
          successCb: () => {
            this.snackbar = true
            this.snackColor = 'success'
            this.snackMessage = 'RISC grades populated succesfully!'
            this.getQualifiersDataAndConfiguration(this.tenantId ? this.tenantId : this.currentUserGroup.id)           
          },
          failedCb: (error) => {
            this.warningModel = true
            this.errorTitle = 'Failed to populated the RISC grades!'
            this.errorMessage = error
          },
        }
        this.populateDefaultQualifers(options)
      },
    },
  }
</script>

<style lang="stylus" scoped>
</style>